import { BodyText } from '@postscript/components';
import React from 'react';
import styled from 'styled-components';

import { RuntimeContextProduct } from '../types';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-1);
`;

const ItemButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-1);
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--link-color);

  &:hover {
    color: var(--link-color-hover);
  }
`;

// Shopify handles are kebab-case slugs; render them as title-cased words so
// a missing product title doesn't surface raw "womens-sandals-cushion" text.
const humanizeHandle = (handle: string): string =>
  handle
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Stored snapshots can omit the product title (null or empty string) — fall
// back to a humanized handle so the row still identifies the product. Null
// when the product has nothing displayable (including a separator-only
// handle that humanizes to ""); callers skip those rows.
export const getProductDisplayTitle = (
  product: RuntimeContextProduct,
): string | null => {
  if (product.title) return product.title;
  if (!product.handle) return null;
  return humanizeHandle(product.handle) || null;
};

interface ProductListProps {
  products: RuntimeContextProduct[];
  onProductClick?: (productId: string | null) => void;
}

export const ProductList: React.FC<ProductListProps> = ({
  products,
  onProductClick,
}) => (
  <List>
    {products.map((product) => {
      const title = getProductDisplayTitle(product);
      if (!title) return null;
      const key = product.id ?? (product.handle || title);
      return onProductClick ? (
        <ItemButton
          key={key}
          type="button"
          onClick={() => onProductClick(product.id)}
        >
          <BodyText size="small">{title}</BodyText>
        </ItemButton>
      ) : (
        <Item key={key}>
          <BodyText size="small">{title}</BodyText>
        </Item>
      );
    })}
  </List>
);
